<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div>
    <div class="page-head">
      <div>
        <h2>发放记录</h2>
        <p>选择待发项目，可一次或分次发放；确认后生成资金流出</p>
      </div>
      <el-button
        type="primary"
        @click="open"
      >
        新建发放单
      </el-button>
    </div>
    <el-card shadow="never">
      <el-table
        :data="rows"
        v-loading="loading"
        row-key="id"
      >
        <el-table-column type="expand">
          <template #default="{ row }">
            <el-table :data="row.details">
              <el-table-column
                prop="user_name"
                label="员工"
              />
              <el-table-column
                prop="payable_type"
                label="类型"
              />
              <el-table-column
                prop="amount"
                label="本次发放"
              />
            </el-table>
          </template>
        </el-table-column>
        <el-table-column
          prop="payout_date"
          label="发放日期"
        />
        <el-table-column label="金额">
          <template #default="{ row }">¥ {{ money(row.amount) }}</template>
        </el-table-column>
        <el-table-column
          prop="account_name"
          label="账户"
        />
        <el-table-column
          prop="payee"
          label="收款对象"
        />
        <el-table-column
          prop="status"
          label="状态"
        >
          <template #default="{ row }">
            <StatusTag :status="row.status" />
          </template>
        </el-table-column>
        <el-table-column
          label="操作"
          width="220"
        >
          <template #default="{ row }">
            <el-button
              v-if="row.status === 'DRAFT'"
              link
              type="primary"
              @click="confirm(row)"
            >
              确认
            </el-button>
            <el-button
              v-if="row.status === 'CONFIRMED'"
              link
              type="danger"
              @click="voidRow(row)"
            >
              冲销
            </el-button>
            <VoucherButton
              biz-type="PAYOUT"
              :biz-id="row.id"
            />
          </template>
        </el-table-column>
      </el-table>
    </el-card>
    <el-dialog
      v-model="dialog"
      title="新建发放单"
      width="900px"
    >
      <el-form label-width="90px">
        <el-row :gutter="16">
          <el-col :span="8">
            <el-form-item label="发放日期">
              <el-date-picker
                v-model="form.payoutDate"
                type="datetime"
                value-format="YYYY-MM-DD HH:mm:ss"
              />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="资金账户">
              <el-select v-model="form.accountId">
                <el-option
                  v-for="a in activeAccounts"
                  :key="a.id"
                  :label="a.name"
                  :value="a.id"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="支付方式">
              <el-select v-model="form.paymentMethod">
                <el-option
                  v-for="m in methods"
                  :key="m.code"
                  :label="m.name"
                  :value="m.code"
                />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item
          label="发放员工"
          required
        >
          <el-select
            v-model="form.userId"
            filterable
            style="width: 100%"
            @change="changeUser"
          >
            <el-option
              v-for="u in payableUsers"
              :key="u.id"
              :label="u.name"
              :value="u.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="收款对象">
          <el-input v-model="form.payee" />
        </el-form-item>
        <el-form-item label="应发项目">
          <el-table :data="filteredPayables">
            <el-table-column
              prop="user_name"
              label="员工"
            />
            <el-table-column
              prop="type"
              label="类型"
            />
            <el-table-column label="未发">
              <template #default="{ row }">
                ¥ {{ money(row.remaining_amount) }}
              </template>
            </el-table-column>
            <el-table-column
              label="本次金额"
              width="180"
            >
              <template #default="{ row }">
                <el-input-number
                  v-model="amounts[row.id]"
                  :min="
                    Number(row.remaining_amount) < 0
                      ? Number(row.remaining_amount)
                      : 0
                  "
                  :max="
                    Number(row.remaining_amount) < 0
                      ? 0
                      : Number(row.remaining_amount)
                  "
                  :precision="2"
                  :controls="false"
                />
              </template>
            </el-table-column>
          </el-table>
        </el-form-item>
        <el-form-item label="合计">¥ {{ money(totalAmount) }}</el-form-item>
        <el-form-item label="备注">
          <el-input v-model="form.note" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialog = false">取消</el-button>
        <el-button
          type="primary"
          :loading="saving"
          @click="save"
        >
          保存草稿
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>
<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { ledgerApi, organizationApi } from '../api'
import StatusTag from '../components/StatusTag.vue'
import VoucherButton from '../components/VoucherButton.vue'
const rows = ref([]),
  available = ref([]),
  accounts = ref([]),
  methods = ref([]),
  loading = ref(false),
  dialog = ref(false),
  saving = ref(false),
  amounts = reactive({}),
  form = reactive({
    payoutDate: '',
    userId: null,
    accountId: null,
    paymentMethod: 'BANK',
    payee: '',
    note: '',
  })
const activeAccounts = computed(() => accounts.value.filter((a) => a.enabled))
const payableUsers = computed(() => {
  const users = new Map()
  available.value.forEach((item) =>
    users.set(item.user_id, { id: item.user_id, name: item.user_name })
  )
  return Array.from(users.values())
})
const filteredPayables = computed(() =>
  available.value.filter((item) => item.user_id === form.userId)
)
const totalAmount = computed(() =>
  Object.values(amounts).reduce((s, v) => s + Number(v || 0), 0)
)
const money = (v) => Number(v || 0).toFixed(2)
const load = async () => {
  loading.value = true
  try {
    const [p, pa, a, m] = await Promise.all([
      ledgerApi.payouts(),
      ledgerApi.payables({ page: 1, pageSize: 100, status: 'OPEN' }),
      ledgerApi.accounts(),
      organizationApi.paymentMethods(),
    ])
    rows.value = p || []
    available.value = pa.items || []
    accounts.value = a || []
    methods.value = m || []
  } finally {
    loading.value = false
  }
}
const open = () => {
  Object.keys(amounts).forEach((k) => delete amounts[k])
  form.payoutDate = new Date().toISOString().slice(0, 19).replace('T', ' ')
  form.userId = null
  dialog.value = true
}
const changeUser = () => {
  Object.keys(amounts).forEach((k) => delete amounts[k])
  form.payee = payableUsers.value.find((u) => u.id === form.userId)?.name || ''
}
const save = async () => {
  const details = filteredPayables.value
    .filter((r) => Number(amounts[r.id]) !== 0)
    .map((r) => ({ payableId: r.id, amount: amounts[r.id] }))
  if (!form.accountId || !details.length || totalAmount.value <= 0)
    return ElMessage.warning('请选择账户，且抵扣调整后的实际发放合计必须大于0')
  saving.value = true
  try {
    await ledgerApi.createPayout({
      ...form,
      amount: totalAmount.value,
      details,
    })
    dialog.value = false
    ElMessage.success('发放草稿已保存')
    load()
  } catch (e) {
    ElMessage.error(e?.message || '保存失败')
  } finally {
    saving.value = false
  }
}
const confirm = async (r) => {
  try {
    await ElMessageBox.confirm(
      '确认后将扣减资金账户并冲抵应发，是否继续？',
      '确认'
    )
    await ledgerApi.confirmPayout(r.id)
    load()
  } catch (e) {
    if (e !== 'cancel') ElMessage.error(e?.message || '确认失败')
  }
}
const voidRow = async (r) => {
  try {
    const { value } = await ElMessageBox.prompt('请输入冲销原因', '冲销发放', {
      inputValidator: (v) => !!v?.trim() || '原因不能为空',
    })
    await ledgerApi.voidPayout(r.id, value)
    load()
  } catch (e) {
    if (e !== 'cancel') ElMessage.error(e?.message || '冲销失败')
  }
}
onMounted(load)
</script>
<style scoped>
.page-head {
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
}
.page-head h2 {
  font-size: 24px;
}
.page-head p {
  color: #8492a6;
  margin-top: 6px;
}
.el-card {
  border: 0;
}
</style>
